import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { EntityListActions } from 'actions';
import VirtualizedList from 'components/VirtualizedList';
import { CONVERSATIONS } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';

import useConversations from '../../hooks/useConversations';
import { getUnreadMessages } from '../../utils/conversations';
import { getActivitiesWithUnreadMessages } from '../../utils/timeline';
import { ListLoader } from '../Loaders';
import ConversationsListRow from './ConversationsListRow';
import './styles.scss';

const mapStateToProps = (state) => {
    const unreadMessages = state?.messages?.unread?.all?.entities || {};
    const entityList = state?.entityList;
    const conversationsList = entityList.conversations || null;
    const conversations = conversationsList?.data || [];
    const matchingName = state?.entityFilters?.activities?.filters?.matchingName?.value;

    const loading = conversationsList?.loading || false;
    const offset = conversationsList?.offset || 0;
    return {
        conversations,
        loading,
        unreadMessages,
        offset,
        matchingName,
    };
};

const mapDispatchToProps = (dispatch) => ({
    updateEntityList: bindActionCreators(EntityListActions, dispatch).updateRowFields,
    init: bindActionCreators(EntityListActions, dispatch).init,
    setOffset: bindActionCreators(EntityListActions, dispatch).setOffset,
});

const ConversationsList = memo(
    ({
        conversations,
        count,
        init,
        loading,
        unreadMessages,
        offset,
        setOffset,
        setUnreadMessages,
        updateEntityList,
        matchingName,
        getConversationsWidget,
    }) => {
        const domContent = useRef(null);
        const unreadMessagesRef = useRef({});
        const hasRequestedInitialLoad = useRef(false);
        const theme = useTheme();

        const { getConversations } = useConversations({
            init,
            offset,
            setOffset,
            getConversationsWidget,
        });

        const handleUnreadMessages = useCallback(
            (unreadMessages) => {
                conversations.forEach((conversation) => {
                    const { Id } = conversation;

                    updateEntityList(CONVERSATIONS, Id, {
                        UnreadConversations: getUnreadMessages(conversation, unreadMessages) || 0,
                    });
                });
            },
            [conversations, updateEntityList],
        );

        useEffect(() => {
            if (
                isEqual(unreadMessages, unreadMessagesRef.current) ||
                loading ||
                conversations?.length === 0
            )
                return;

            unreadMessagesRef.current = unreadMessages;

            const activitiesWithUnreadMessages = getActivitiesWithUnreadMessages(unreadMessages);

            if (Object.entries(activitiesWithUnreadMessages).length > 0) {
                handleUnreadMessages(activitiesWithUnreadMessages);
                setUnreadMessages(activitiesWithUnreadMessages);
            } else {
                setUnreadMessages({});
            }
        }, [unreadMessages, handleUnreadMessages, setUnreadMessages, loading, conversations]);

        useEffect(() => {
            if (hasRequestedInitialLoad.current || getConversationsWidget) return;

            hasRequestedInitialLoad.current = true;
            getConversations({ resetOffset: true });
        }, [getConversations, getConversationsWidget]);

        useEffect(() => {
            return () => {
                domContent.current = null;
            };
        }, []);

        const renderRow = useCallback(
            ({ index, data }) => {
                return (
                    <ConversationsListRow
                        key={data.Id}
                        conversation={data}
                        matchingName={matchingName}
                    />
                );
            },
            [matchingName],
        );

        const loaderBox = useMemo(() => {
            return (
                <div className="fm-conversations-list__loader-box">
                    <Text
                        className="fm-conversations-list__loader-box-text"
                        type="subtitle1"
                        color={theme.colors.primary.white}
                    >
                        {getLiteral('wait_loading')}
                    </Text>
                </div>
            );
        }, [theme]);

        const defaultLoaderWidth = useMemo(() => {
            const body = document.querySelector('body');
            const bodyWidth = body.offsetWidth;
            const navigationEl = document.querySelector('.react-navigation-layout');
            const navigationWidth = navigationEl?.offsetWidth || 0;
            const detailEl = document.querySelector('.fm-activities__detail');
            const detailWidth = detailEl?.offsetWidth || 0;

            const staticNavigationWidth = 224;
            const staticDetailWidth = (bodyWidth - navigationWidth) * 0.5;

            if (navigationWidth && detailWidth) return bodyWidth - navigationWidth - detailWidth;
            return bodyWidth - staticNavigationWidth - staticDetailWidth;
        }, []);

        const defaultLoaderHeight = useMemo(() => {
            const body = document.querySelector('body');
            const bodyHeight = body.offsetHeight;
            const headersHeight = 64;
            return bodyHeight - headersHeight * 2;
        }, []);

        const shouldRenderListLoader = useMemo(
            () => !conversations?.length || (!hasRequestedInitialLoad.current && loading),
            [conversations?.length, loading],
        );

        return (
            <div className="fm-conversations-list" ref={domContent}>
                {shouldRenderListLoader && (
                    <ListLoader
                        width={domContent.current?.offsetWidth || defaultLoaderWidth}
                        height={domContent.current?.offsetHeight || defaultLoaderHeight}
                    />
                )}
                {loading && loaderBox}
                <VirtualizedList
                    entity={CONVERSATIONS}
                    containerKey="fm-conversations-list"
                    renderRow={renderRow}
                    total={count > conversations.length ? count : conversations.length}
                    onLoad={getConversations}
                    skipInitialLoad={!getConversationsWidget}
                    threshold={5}
                    data={conversations}
                />
            </div>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(ConversationsList);
